import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import StyledRichText from 'components/StyledRichText';
import { blockPadV, blockPadH, blockW } from 'theme/mixins/padding';
import type { AboutItem as T } from './types';
import TechItem from 'components/TechItem';

const Wrapper = styled.div`
  ${() => blockPadV()};
  ${() => blockPadH()};
`;

const Container = styled.div`
  ${() => blockW()};
`;

const ContentWrapper = styled.div`
  max-width: 53.75rem;
`;

const Title = styled.h4`
  opacity: 1;
`;

const Description = styled.div`
  margin-bottom: 1.5rem;
  ${({ theme }) => theme.mq.lg`
    margin-bottom: 3.75rem;
  `}
`;

const TechsContainer = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  gap: 1rem;
  justify-content: space-around;

  ${({ theme }) => theme.mq.sm`
    column-gap: 3rem;
    row-gap: 2rem;
  `}

  ${({ theme }) => theme.mq.md`
    grid-template-columns: repeat(6, auto);
    justify-content: start;
  `}
`;

interface IGradientBackground {
  gradient: string;
  title: string;
}

const GradientBackground = styled.div<IGradientBackground>`
  background: ${({ gradient }) => gradient};
  ${({ title }) => {
    switch (title) {
      case 'Ecommerce Solutions':
        return 'background-position: 50% 50%;';
      case 'SaaS Development':
        return `
          mix-blend-mode: normal;
        `;
      default:
        return '';
    }
  }}
  border-radius: 1.25rem;
  padding: 2.5rem 1.5rem;

  ${({ theme }) => theme.mq.md`
    padding: 3.25rem 2.5rem;
  `}
`;

const AboutItem: React.FC<T> = ({ title, styledRichText, technologies }) => {
  const [gradient, setGradient] = useState<string>('');

  const determineGradient = () => {
    switch (title) {
      case 'Ecommerce Solutions':
        setGradient(
          'linear-gradient(0deg, rgba(16, 19, 82, 0.4), rgba(16, 19, 82, 0.4)), linear-gradient(80deg, rgb(131, 100, 154) 0%, rgb(215,92,84) 100%)'
        );
        break;
      case 'Marketing Sites':
        setGradient(
          'linear-gradient(0deg, rgba(16, 19, 82, 0.4), rgba(16, 19, 82, 0.4)), linear-gradient(180deg, #024FB6 0%, #D0BFEC 100%)'
        );
        break;
      case 'SaaS Development':
        setGradient(
          'linear-gradient(110.71deg, rgba(47, 171, 156, 0.4) 8.66%, rgba(46, 106, 222, 0.4) 64.62%)'
        );
        break;
      case 'Data Visualization':
        setGradient('linear-gradient(180deg, #0D3BBB 0%, #22C2E1 100%)');
        break;
      default:
        return;
    }
  };

  useEffect(() => determineGradient(), []);

  return (
    <Wrapper>
      <Container>
        <GradientBackground gradient={gradient} title={title || ''}>
          <ContentWrapper>
            <Title>{title}</Title>
            <Description>
              <StyledRichText pFormat={styledRichText?.pFormat} content={styledRichText?.body} />
            </Description>
            <TechsContainer>
              {technologies?.map((tech, i) => (
                <TechItem key={`${i}--${tech?.name}`} name={tech?.name || ''} />
              ))}
            </TechsContainer>
          </ContentWrapper>
        </GradientBackground>
      </Container>
    </Wrapper>
  );
};

export default AboutItem;
